import { useState } from "react";
import { Button, Input, Label, CustomSelect } from "@/components";
import { ArrowRight, Copy, CopyCheck, Zap } from "lucide-react";

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export default function WhatsAppWidgetGenerator() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [brandName, setBrandName] = useState("");
  // const [buttonLabel, setButtonLabel] = useState("Chat with us");
  const [buttonPosition, setButtonPosition] = useState("bottom-left");
  const [buttonStyle, setButtonStyle] = useState("whatsapp"); // 'whatsapp' or 'message'
  const [welcomeText, setWelcomeText] = useState("Hello! How can we help?");
  const [prefilledMessage, setPrefilledMessage] = useState(
    "Hello! I'd like to know more about your services.",
  );
  const [openWidgetDefault, setOpenWidgetDefault] = useState(false);
  const [brandImageUrl, setBrandImageUrl] = useState("");

  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [message, setMessage] = useState("Chat with us 🚀");
  const [buttonColor, setButtonColor] = useState("#185db8");
  const [copied, setCopied] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const [email, setEmail] = useState("");

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) return false;

    return true;
  };

  const generateCode = () => {
    let isValid = true;
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      isValid = false;
    } else {
      setPhoneError("");
    }

    if (!email.trim()) {
      setEmailError("Business email is required");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Enter a valid business email");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!isValid) return;

    const position = buttonPosition === "bottom-left" ? "left" : "right";
    const marginRight = buttonPosition === "bottom-left" ? "0" : "20";
    const marginLeft = buttonPosition === "bottom-left" ? "20" : "0";

    const code = `<script>
  var url = 'https://quick-assists-media.s3.ap-south-1.amazonaws.com/whatsapp-wati-script/Widget.js';
  var s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = url;
  var options = {
    "enabled": true,
    "chatButtonSetting": {
      "backgroundColor": "${buttonColor}",
      "ctaText": "${message}",
      "borderRadius": "25",
      "marginLeft": "${marginLeft}",
      "marginRight": "${marginRight}",
      "marginBottom": "20",
      ctaIconWATI: ${buttonStyle === "message"},
      "position": "${position}"
    },
    "brandSetting": {
      "brandName": "${brandName}",
      "brandSubTitle": "undefined",
      "brandImg": "${brandImageUrl}",
      "welcomeText": "${welcomeText.replace(/\n/g, "\\n")}",
      "messageText": "${prefilledMessage}",
      "backgroundColor": "${buttonColor}",
      "ctaText": "${message}",
      "borderRadius": "25",
      "autoShow": ${openWidgetDefault},
      "phoneNumber": "${phoneNumber.trim()}"
    }
  };
  s.onload = function() {
    CreateWhatsappChatWidget(options);
  };
  var x = document.getElementsByTagName('script')[0];
  x.parentNode.insertBefore(s, x);
<\/script>`;

    setGeneratedCode(code);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div>
        <div className="flex flex-col gap-6">
          <div className="space-y-8 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {/* Button Style Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Button Style
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Choose from multiple button designs
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Brand Color */}
                <div>
                  <Label htmlFor="brand-color">Brand Color</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600"
                    />
                    <Input
                      id="brand-color-text"
                      type="text"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                      placeholder="#25D366"
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Button Label / CTA Text */}
                <div>
                  <Label htmlFor="button-label">Button Label / CTA Text</Label>
                  <Input
                    id="button-label"
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Chat with us"
                    className="w-full"
                  />
                </div>

                {/* Position */}
                <div>
                  <Label>Position: Bottom-Left or Bottom-Right</Label>
                  <div className="mt-2 flex gap-6 items-center">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="position-bottom-left"
                        name="button-position"
                        value="bottom-left"
                        checked={buttonPosition === "bottom-left"}
                        onChange={(e) => setButtonPosition(e.target.value)}
                        className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:ring-2"
                      />
                      <label
                        htmlFor="position-bottom-left"
                        className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        Bottom Left
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="position-bottom-right"
                        name="button-position"
                        value="bottom-right"
                        checked={buttonPosition === "bottom-right"}
                        onChange={(e) => setButtonPosition(e.target.value)}
                        className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:ring-2"
                      />
                      <label
                        htmlFor="position-bottom-right"
                        className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        Bottom Right
                      </label>
                    </div>
                  </div>
                </div>

                {/* Chat Button Preview */}
                <div>
                  <Label>Chat Button Preview</Label>
                  <div className="mt-2 flex gap-6 items-center">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        id="style-whatsapp"
                        name="button-style"
                        value="whatsapp"
                        checked={buttonStyle === "whatsapp"}
                        onChange={() => {
                          setButtonStyle("whatsapp");
                        }}
                        className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:ring-2"
                      />
                      <div className="flex-1">
                        <button
                          className="p-2 rounded-full text-white font-medium transition-all hover:scale-105"
                          style={{ backgroundColor: buttonColor }}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        id="style-message"
                        name="button-style"
                        value="message"
                        checked={buttonStyle === "message"}
                        onChange={() => {
                          setButtonStyle("message");
                        }}
                        className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:ring-2"
                      />
                      <div className="flex-1">
                        <button
                          className="p-2 rounded-full text-white font-medium transition-all hover:scale-105"
                          style={{ backgroundColor: buttonColor }}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tool Customisation Options Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Tool Customisation Options
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Change your brand name, welcome text and default user message
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Share your Email ID */}
                <div>
                  <Label htmlFor="email-id">Share your Email ID</Label>
                  <Input
                    id="email-id"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEmail(value);

                      if (!value.trim()) {
                        setEmailError("Business email is required");
                      } else if (!validateEmail(value)) {
                        setEmailError("Enter a valid business email");
                      } else {
                        setEmailError("");
                      }
                    }}
                    placeholder="business@company.com"
                    className="w-full"
                  />
                  {emailError && (
                    <p className="text-sm text-red-500 mt-1">{emailError}</p>
                  )}
                </div>

                {/* Brand Name */}
                <div>
                  <Label htmlFor="brand-name">
                    Brand Name: Your business name shown on the widget
                  </Label>
                  <Input
                    id="brand-name"
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="Your Company Name"
                    className="w-full"
                  />
                </div>

                {/* Phone Number with Country Code */}
                <div>
                  <Label htmlFor="phone-number">
                    Phone Number with Country Code
                  </Label>
                  <Input
                    id="phone-number"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPhoneNumber(value);

                      if (value.trim()) {
                        setPhoneError("");
                      }
                    }}
                    placeholder="1234567890"
                    className="w-full"
                  />
                  {phoneError && (
                    <p className="text-sm text-red-500 mt-1">{phoneError}</p>
                  )}
                </div>

                {/* Welcome Text */}
                <div>
                  <Label htmlFor="welcome-text">
                    Welcome Text: Short greeting shown before the chat opens (max
                    40 chars)
                  </Label>
                  <Input
                    id="welcome-text"
                    type="text"
                    value={welcomeText}
                    onChange={(e) => setWelcomeText(e.target.value)}
                    placeholder="Hello! How can we help?"
                    maxLength={40}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {welcomeText.length}/40 characters
                  </p>
                </div>

                {/* Pre-filled Message */}
                <div className="md:col-span-2">
                  <Label htmlFor="prefilled-message">
                    Pre-filled Message: Optional message auto-sent when visitor
                    opens chat
                  </Label>
                  <textarea
                    id="prefilled-message"
                    value={prefilledMessage}
                    onChange={(e) => setPrefilledMessage(e.target.value)}
                    placeholder="Hello! I'd like to know more about your services."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>

                {/* Open Widget by Default */}
                <div>
                  <Label>
                    Open Widget by Default: Choose if the widget opens
                    automatically or on click
                  </Label>
                  <CustomSelect
                    value={openWidgetDefault.toString()}
                    onChange={(value) => setOpenWidgetDefault(value === "true")}
                    options={[
                      { value: "false", label: "Open on Click" },
                      { value: "true", label: "Open Automatically" },
                    ]}
                    className="w-full"
                    placeholder="Select option"
                  />
                </div>

                {/* Brand Image URL */}
                <div>
                  <Label htmlFor="brand-image-url">
                    Brand Image URL: Add your logo to the widget
                  </Label>
                  <Input
                    id="brand-image-url"
                    type="url"
                    value={brandImageUrl}
                    onChange={(e) => setBrandImageUrl(e.target.value)}
                    placeholder="https://yourwebsite.com/logo.png"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <Button
              className="ms-auto flex items-end justify-end"
              onClick={generateCode}
            >
              Generate Widget Code <ArrowRight size={16} />
            </Button>
          </div>
          {/* Generated Code Output */}
          {generatedCode && (
            <div className="flex flex-col justify-between w-full gap-2 bg-white p-6 rounded-md border border-gray-200 dark:border-gray-600 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-gray-900 dark:text-gray-100 font-bold">Your Generated Widget Code</h3>
                <button
                  onClick={copyCode}
                  className={cn(
                    "group relative flex items-center gap-2 px-3 py-2 rounded-sm cursor-pointer font-medium transition-all duration-300 text-xs",
                    copied
                      ? "bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/20"
                      : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20",
                  )}
                >
                  {/* <button
                  onClick={copyCode}
                  className="group relative flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer text-sm font-bold transition-all duration-300" 
                > */}
                  {copied ? (
                    <>
                      <CopyCheck
                        size={14}
                        strokeWidth={3}
                        className="animate-in zoom-in duration-300"
                      />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy
                        size={14}
                        strokeWidth={2}
                        className="group-hover:scale-110 transition-transform"
                      />
                      <span>Copy Code</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="text-green-600 text-sm overflow-x-auto whitespace-pre-wrap break-all">
                <code>{generatedCode}</code>
              </pre>
              <p className="text-yellow-600 dark:text-yellow-400/80  text-sm mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-600 dark:border-yellow-400/60 p-4 rounded-sm flex  gap-2">
                <span className="font-semibold text-yellow-700 dark:text-yellow-500">Note:</span> Paste this code before the closing &lt;/body&gt; tag on your
                website.
              </p>

              <div className="divider flex items-center">
                <hr className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap px-2">OR</span>
                <hr className="flex-1 border-t border-gray-300 dark:border-gray-600" />
              </div>

              <div className="bg-green-50 dark:bg-green-600/10 rounded-md border border-[#025d4b]/20 dark:border-gray-700 p-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="max-w-12 w-12 h-12 bg-[#025d4b] rounded-full flex items-center justify-center shadow-lg">
                      <Zap color="white" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <h3 className="font-bold text-gray-900 dark:text-white">Automate conversations when someone clicks your link</h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">TheQuickAssist handles instant auto-replies, lead capture, and full WhatsApp CRM.</p>
                    </div>
                  </div>

                  <Button
                    className="flex items-center gap-2 bg-[#025d4b] hover:text-white hover:border-[#025d4b] text-white  transform hover:scale-105 transition-all duration-300 rounded-full font-semibold"
                  >
                    Start free <ArrowRight size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
